<template>
  <div v-bind="rootProps" :class="classes">
    <slot />
  </div>
</template>

<script setup lang="ts">
import { computed, type HTMLAttributes } from 'vue'
import type { IconSquareSize, IconSquareVariant } from './types'
import { reactiveOmit } from '@vueuse/core'

type Props = {
  /**
   * Size of the icon in pixels. It could be small, 'medium', or 'large'.
   * This will set both width and height of the icon.
   * @type {IconSquareSize}
   * @example small
   * @default small
   */
  size?: IconSquareSize

  /**
   * Variant of the icon. It will set the color and background color.
   * @type {IconSquareVariant}
   * @example 'gainsboro'
   * @default 'gainsboro'
   */
  variant?: IconSquareVariant
} & /* @vue-ignore */ HTMLAttributes

const props = withDefaults(defineProps<Props>(), {
  size: 'small',
  variant: 'gainsboro',
})

const rootProps = reactiveOmit(props, ['size', 'variant'])

const classes = computed(() => {
  return {
    'sb-icon-square': true,
    [`sb-icon-square--${props.variant}`]: props.variant,
    [`sb-icon-square--${props.size}`]: props.size,
  }
})
</script>

<style scoped lang="scss">
$variants: (
  'gainsboro',
  'pink-lace',
  'pale-lavender',
  'lavender',
  'water',
  'green-cyan',
  'papaya-whip',
  'lumber',
  'light-red',
  'white'
);
$sizes: (
  'small': 24px,
  'medium': 36px,
  'large': 48px,
);

.sb-icon-square {
  @each $variant in $variants {
    &--#{$variant} {
      color: var(--sb-color-accent-content-#{$variant});
      background-color: var(--sb-color-accent-background-#{$variant});
    }
  }

  @each $name, $sizeValue in $sizes {
    &--#{$name} {
      width: $sizeValue;
      height: $sizeValue;
    }
  }

  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: $sb-border-width-lg;
}
</style>
